const API_URL =
  process.env.NODE_ENV === 'production'
    ? '/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:5003/api');

function getToken() {
  return localStorage.getItem('token');
}

async function request(endpoint, { method = 'GET', body, token, ...custom } = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
  const res = await fetch(`${API_URL}${endpoint}`, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
    ...custom,
  });
  if (!res.ok) {
    let error = 'Unknown error';
    try {
      const data = await res.json();
      error = data.error || JSON.stringify(data);
    } catch {}
    throw new Error(error);
  }
  return res.json();
}

// Auth
export async function login(email, password) {
  return request('/auth/login', { method: 'POST', body: { email, password } });
}
export async function register(name, email, password, role, accepted_terms, terms_version) {
  return request('/auth/register', { method: 'POST', body: { name, email, password, role, accepted_terms, terms_version } });
}
export async function updateConsent(userId) {
  return request(`/auth/users/${userId}/consent`, { method: 'POST', token: getToken() });
}

// Password Reset
export async function forgotPassword(email) {
  return request('/auth/forgot-password', { method: 'POST', body: { email } });
}

export async function verifyResetCode(email, code) {
  return request('/auth/verify-reset-code', { method: 'POST', body: { email, code } });
}

export async function resetPassword(email, code, newPassword) {
  return request('/auth/reset-password', { method: 'POST', body: { email, code, newPassword } });
}

// Skis
export async function getSkis() {
  return request('/skis', { token: getToken() });
}
export async function addSki(ski) {
  return request('/skis', { method: 'POST', body: ski, token: getToken() });
}
export async function updateSki(skiId, ski) {
  return request(`/skis/${skiId}`, { method: 'PUT', body: ski, token: getToken() });
}
export async function deleteSki(skiId) {
  return request(`/skis/${skiId}`, { method: 'DELETE', token: getToken() });
}
export async function getSkiByQr(qrCode) {
  return request(`/skis/qr/${encodeURIComponent(qrCode)}`);
}
export async function regenerateSkiQr(skiId) {
  return request(`/skis/${skiId}/qr-code`, { method: 'POST', token: getToken() });
}
export async function bulkAddSkis(skis) {
  return request('/skis/bulk', { method: 'POST', body: { skis }, token: getToken() });
}
export async function updateSkiOrder(skiOrders) {
  return request('/skis/order', { method: 'PUT', body: { skiOrders }, token: getToken() });
}

// Teams
export async function getTeams() {
  return request('/teams', { token: getToken() });
}
export async function joinTeam(teamId) {
  return request(`/teams/${teamId}/join`, { method: 'POST', token: getToken() });
}
export async function getMyTeams() {
  return request('/teams/my', { token: getToken() });
}
export async function getAllTeams(search) {
  const url = search ? `/teams?search=${encodeURIComponent(search)}` : '/teams';
  return request(url, { token: getToken() });
}
export async function addTeam(team) {
  return request('/teams', { method: 'POST', body: team, token: getToken() });
}
export async function deleteTeam(teamId) {
  return request(`/teams/${teamId}`, { method: 'DELETE', token: getToken() });
}
export async function getTeamMembers(teamId) {
  return request(`/teams/${teamId}/members`, { token: getToken() });
}
export async function approveMember(teamId, userId) {
  return request(`/teams/${teamId}/approve`, { method: 'POST', body: { userId }, token: getToken() });
}
export async function rejectMember(teamId, userId) {
  return request(`/teams/${teamId}/reject`, { method: 'POST', body: { userId }, token: getToken() });
}
export async function removeMember(teamId, userId) {
  return request(`/teams/${teamId}/members/${userId}`, { method: 'DELETE', token: getToken() });
}

// Races
export async function getRaces() {
  return request('/races', { token: getToken() });
}
export async function addRace(race) {
  return request('/races', { method: 'POST', body: race, token: getToken() });
}
export async function updateRace(raceId, race) {
  return request(`/races/${raceId}`, { method: 'PUT', body: race, token: getToken() });
}
export async function deleteRace(raceId) {
  return request(`/races/${raceId}`, { method: 'DELETE', token: getToken() });
}

// Tests
export async function getTests() {
  return request('/tests', { token: getToken() });
}
export async function addTest(test) {
  return request('/tests', { method: 'POST', body: test, token: getToken() });
}
export function getTestDetail(testId) {
  return request(`/tests/${testId}`, { token: getToken() });
}
export async function updateTest(testId, test) {
  return request(`/tests/${testId}`, { method: 'PUT', body: test, token: getToken() });
}
export async function deleteTest(testId) {
  return request(`/tests/${testId}`, { method: 'DELETE', token: getToken() });
}

// Add more as needed (tests, races, etc)
export async function getAthleteSkis(teamId, athleteId) {
  return request(`/teams/${teamId}/athletes/${athleteId}/skis`, { token: getToken() });
}
export async function addAthleteSki(teamId, athleteId, ski) {
  return request(`/teams/${teamId}/athletes/${athleteId}/skis`, { method: 'POST', body: ski, token: getToken() });
}
export async function updateAthleteSki(teamId, athleteId, skiId, ski) {
  return request(`/teams/${teamId}/athletes/${athleteId}/skis/${skiId}`, { method: 'PUT', body: ski, token: getToken() });
}
export async function deleteAthleteSki(teamId, athleteId, skiId) {
  return request(`/teams/${teamId}/athletes/${athleteId}/skis/${skiId}`, { method: 'DELETE', token: getToken() });
}
export async function bulkAddAthleteSkis(teamId, athleteId, skis) {
  return request(`/teams/${teamId}/athletes/${athleteId}/skis/bulk`, { method: 'POST', body: { skis }, token: getToken() });
}
export async function getAthleteTests(teamId, athleteId) {
  return request(`/teams/${teamId}/athletes/${athleteId}/tests`, { token: getToken() });
}
export async function getAthleteRaces(teamId, athleteId) {
  return request(`/teams/${teamId}/athletes/${athleteId}/races`, { token: getToken() });
}

// Waxing Diary
export async function getMyWaxingDiary() {
  return request('/waxing-diary/my', { token: getToken() });
}
export async function addWaxingDiaryEntry(entry) {
  return request('/waxing-diary', { method: 'POST', body: entry, token: getToken() });
}
export async function updateWaxingDiaryEntry(entryId, entry) {
  return request(`/waxing-diary/${entryId}`, { method: 'PUT', body: entry, token: getToken() });
}
export async function deleteWaxingDiaryEntry(entryId) {
  return request(`/waxing-diary/${entryId}`, { method: 'DELETE', token: getToken() });
}
export async function getWaxingDiaryEntry(entryId) {
  return request(`/waxing-diary/${entryId}`, { token: getToken() });
}

// Sharing functionality
export async function shareWaxingEntry(entryId, teamId) {
  return request(`/waxing-diary/${entryId}/share`, { method: 'POST', body: { team_id: teamId }, token: getToken() });
}
export async function unshareWaxingEntry(entryId, teamId) {
  return request(`/waxing-diary/${entryId}/share/${teamId}`, { method: 'DELETE', token: getToken() });
}
export async function getSharedWaxingEntries(teamId) {
  return request(`/waxing-diary/team/${teamId}/shared`, { token: getToken() });
}
export async function getSharedTeams(entryId) {
  return request(`/waxing-diary/${entryId}/shared-teams`, { token: getToken() });
}

// Ski Requests API
export async function createSkiRequest(teamId, data) {
  return request(`/teams/${teamId}/ski-requests`, { method: 'POST', body: data, token: getToken() });
}

export async function getSkiRequests(teamId, eventId = null) {
  const url = eventId ? `/teams/${teamId}/ski-requests?eventId=${eventId}` : `/teams/${teamId}/ski-requests`;
  return request(url, { token: getToken() });
}

export async function submitSkiRequest(requestId, ski_ids) {
  return request(`/ski-requests/ski-requests/${requestId}/submit`, { method: 'POST', body: { ski_ids }, token: getToken() });
}

export async function getSkiRequestSubmissions(requestId) {
  return request(`/ski-requests/ski-requests/${requestId}/submissions`, { token: getToken() });
}

export async function deleteSkiRequest(teamId, requestId) {
  return request(`/teams/${teamId}/ski-requests/${requestId}`, { method: 'DELETE', token: getToken() });
} 

// Event API
export async function getTeamEvents(teamId) {
  // List events for a team
  return request(`/events/teams/${teamId}/events`, { token: getToken() });
}

export async function createTeamEvent(teamId, event) {
  // Create a new event (coach)
  return request(`/events/teams/${teamId}/events`, { method: 'POST', body: event, token: getToken() });
}

export async function getEvent(eventId) {
  // Get event details
  return request(`/events/events/${eventId}`, { token: getToken() });
}

export async function signupForEvent(eventId) {
  // Athlete sign up for event
  return request(`/events/events/${eventId}/signup`, { method: 'POST', token: getToken() });
}

export async function leaveEvent(eventId) {
  // Athlete leave event
  return request(`/events/events/${eventId}/signup`, { method: 'DELETE', token: getToken() });
}

export async function getEventSignups(eventId, checkOwn = false) {
  // Get signups for an event (coaches see all, athletes see only their own)
  const url = checkOwn ? `/events/events/${eventId}/signups?checkOwn=true` : `/events/events/${eventId}/signups`;
  return request(url, { token: getToken() });
}

// Billing API
export async function getSubscriptionPlans() {
  return request('/billing/plans');
}

export async function validateCoupon(code) {
  return request('/billing/validate-coupon', { method: 'POST', body: { code } });
}

export async function createTeamWithSubscription(teamData) {
  return request('/teams', { method: 'POST', body: teamData, token: getToken() });
}

export async function getTeamSubscription(teamId) {
  return request(`/billing/team/${teamId}/subscription`, { token: getToken() });
}

export async function getTeamUsage(teamId) {
  return request(`/billing/team/${teamId}/usage`, { token: getToken() });
}

export async function upgradeTeamPlan(teamId, planId, billingCycle) {
  return request(`/billing/team/${teamId}/upgrade`, { method: 'POST', body: { planId, billingCycle }, token: getToken() });
}

// Admin APIs
export async function adminGetUsers({ q, role, teamId } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (role) params.set('role', role);
  if (teamId) params.set('teamId', teamId);
  const qs = params.toString();
  const url = `/admin/users${qs ? `?${qs}` : ''}`;
  return request(url, { token: getToken() });
}

export async function adminSetUserRole(userId, role) {
  return request(`/admin/users/${userId}/role`, { method: 'POST', body: { role }, token: getToken() });
}

export async function adminGetTeams() {
  return request('/admin/teams', { token: getToken() });
}

export async function adminSetTeamFrozen(teamId, frozen) {
  return request(`/admin/teams/${teamId}/freeze`, { method: 'POST', body: { frozen }, token: getToken() });
}

// Bug reports API
export async function submitBugReport(payload) {
  // If logged-in, attach user_id from localStorage for convenience
  const user = JSON.parse(localStorage.getItem('user'));
  const body = { ...(payload || {}), ...(user?.id ? { user_id: user.id } : {}) };
  return request('/bugs', { method: 'POST', body });
}

export async function adminListBugs({ status, q } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (q) params.set('q', q);
  const url = `/admin/bugs${params.toString() ? `?${params}` : ''}`;
  return request(url, { token: getToken() });
}

export async function adminUpdateBug(id, data) {
  return request(`/admin/bugs/${id}`, { method: 'POST', body: data, token: getToken() });
}

// Metrics API
export async function getAdminMetrics(days = 30) {
  return request(`/admin/metrics?days=${days}`, { token: getToken() });
}

// Wax Tests API
export async function getMyWaxTests() {
  return request('/wax-tests/my', { token: getToken() });
}

export async function getWaxTest(id) {
  return request(`/wax-tests/${id}`, { token: getToken() });
}

export async function createWaxTest(data) {
  return request('/wax-tests', { method: 'POST', body: data, token: getToken() });
}

export async function updateWaxTest(id, data) {
  return request(`/wax-tests/${id}`, { method: 'PUT', body: data, token: getToken() });
}

export async function deleteWaxTest(id) {
  return request(`/wax-tests/${id}`, { method: 'DELETE', token: getToken() });
}

export async function addSkiToWaxTest(testId, data) {
  return request(`/wax-tests/${testId}/skis`, { method: 'POST', body: data, token: getToken() });
}

export async function updateSkiInWaxTest(testId, skiId, data) {
  return request(`/wax-tests/${testId}/skis/${skiId}`, { method: 'PUT', body: data, token: getToken() });
}

export async function deleteSkiFromWaxTest(testId, skiId) {
  return request(`/wax-tests/${testId}/skis/${skiId}`, { method: 'DELETE', token: getToken() });
}

export async function setupWaxTestComparisons(testId) {
  return request(`/wax-tests/${testId}/setup-comparisons`, { method: 'POST', token: getToken() });
}

export async function recordWaxTestComparison(testId, comparisonId, data) {
  return request(`/wax-tests/${testId}/comparisons/${comparisonId}`, { method: 'PUT', body: data, token: getToken() });
}

export async function getNextWaxTestComparisons(testId) {
  return request(`/wax-tests/${testId}/next-comparisons`, { token: getToken() });
}

export async function shareWaxTest(testId, teamId) {
  return request(`/wax-tests/${testId}/share`, { method: 'POST', body: { team_id: teamId }, token: getToken() });
}

export async function unshareWaxTest(testId, teamId) {
  return request(`/wax-tests/${testId}/share/${teamId}`, { method: 'DELETE', token: getToken() });
}

export async function getSharedWaxTests(teamId) {
  return request(`/wax-tests/team/${teamId}/shared`, { token: getToken() });
}

export async function getWaxTestSharedTeams(testId) {
  return request(`/wax-tests/${testId}/shared-teams`, { token: getToken() });
}

// Coach Ski Fleet API
export async function getCoachSkiFleet() {
  return request('/coach-ski-fleet', { token: getToken() });
}

export async function addSkiToFleet(data) {
  return request('/coach-ski-fleet', { method: 'POST', body: data, token: getToken() });
}

export async function updateSkiInFleet(id, data) {
  return request(`/coach-ski-fleet/${id}`, { method: 'PUT', body: data, token: getToken() });
}

export async function deleteSkiFromFleet(id) {
  return request(`/coach-ski-fleet/${id}`, { method: 'DELETE', token: getToken() });
}

// Team Weather Data API (coach only)
export async function createTeamWeatherData(teamId, data) {
  return request(`/teams/${teamId}/weather-data`, { method: 'POST', body: data, token: getToken() });
}

export async function getTeamWeatherData(teamId) {
  return request(`/teams/${teamId}/weather-data`, { token: getToken() });
}

export async function deleteTeamWeatherData(teamId, entryId) {
  return request(`/teams/${teamId}/weather-data/${entryId}`, { method: 'DELETE', token: getToken() });
}

// Available Weather Data API (for athletes)
export async function getAvailableWeatherData(days = 7) {
  return request(`/weather-data/available?days=${days}`, { token: getToken() });
}

// Ski Transfers
export async function createSkiTransfer(skiId, toUserEmail, includeTests, includeRaces) {
  return request('/ski-transfers', { 
    method: 'POST', 
    body: { ski_id: skiId, to_user_email: toUserEmail, include_tests: includeTests, include_races: includeRaces }, 
    token: getToken() 
  });
}

export async function getPendingSkiTransfers() {
  return request('/ski-transfers/pending', { token: getToken() });
}

export async function getSentSkiTransfers() {
  return request('/ski-transfers/sent', { token: getToken() });
}

export async function acceptSkiTransfer(transferId) {
  return request(`/ski-transfers/${transferId}/accept`, { method: 'POST', token: getToken() });
}

export async function rejectSkiTransfer(transferId) {
  return request(`/ski-transfers/${transferId}/reject`, { method: 'POST', token: getToken() });
}

export async function cancelSkiTransfer(transferId) {
  return request(`/ski-transfers/${transferId}/cancel`, { method: 'POST', token: getToken() });
}